// =============================================================================
// app/src/lib/pdf.ts — gerador mínimo de PDF, escrito à mão.
//
// POR QUE À MÃO E NÃO jsPDF: a biblioteca custa ~100 KB comprimidos num bundle
// que na época (ago/2026) tinha 77 KB inteiro — dobraria o download do celular
// do balcão em 4G. E ela não resolveria a parte difícil: renderizar o HTML do
// vale. Com jsPDF a gente ainda posicionaria cada texto à mão, exatamente como
// aqui. O que se paga a mais é só o encanamento do formato, que são as ~150
// linhas abaixo. Mesmo raciocínio do sw.js e do barcode.ts.
//
// O QUE ESTE MÓDULO FAZ: texto (Helvetica e Helvetica-Bold), linhas,
// retângulos preenchidos e caminhos (o símbolo da marca). Não faz imagem nem
// fonte embutida — quando precisar, cresce; enquanto não precisa, não carrega
// peso. VÁRIAS PÁGINAS desde 22/09/2026 (novaPagina), para a lista de contas a
// pagar que vai ao pai do dono: com UMA página o arquivo sai byte a byte o de
// antes — o vale e o orçamento não mudaram (pdf.ouro.test.ts confere).
//
// DUAS ESCOLHAS QUE FACILITAM A VIDA DE QUEM DESENHA O DOCUMENTO:
//
//  * Coordenadas em MILÍMETROS e com o Y crescendo PARA BAIXO, como todo mundo
//    pensa numa folha. O PDF usa pontos com a origem embaixo à esquerda; a
//    conversão fica escondida aqui, num lugar só.
//  * As fontes são as base-14 do PDF (Helvetica), que todo leitor já tem. Sem
//    fonte embutida o arquivo fica em ~4 KB e abre em qualquer aparelho —
//    inclusive no celular do cliente, que é para onde o vale vai por WhatsApp.
// =============================================================================

const MM = 2.834_645_67; // 1 mm em pontos PostScript (72 dpi)

const A4_LARGURA_MM = 210;
const A4_ALTURA_MM = 297;

import type { Segmento } from './svgPath';

export type Alinhamento = 'esquerda' | 'centro' | 'direita';

export interface OpcoesTexto {
  tamanho?: number;
  negrito?: boolean;
  alinhamento?: Alinhamento;
  /** 0 = preto, 1 = branco. O documento é 1 bit na impressora: use 0 ou ~0.4. */
  cinza?: number;
}

export interface OpcoesTraco {
  espessura?: number;
  cinza?: number;
}

// --- Métricas -----------------------------------------------------------------
// Larguras oficiais da Helvetica (unidades de 1/1000 do corpo), ASCII 32..126.
// Servem só para centralizar e alinhar à direita: um erro aqui desloca texto,
// nunca corrompe o arquivo. Por isso a tabela cobre o ASCII e o resto cai no
// truque do `semAcento` abaixo.
const LARGURAS_NORMAL = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];

const LARGURAS_NEGRITO = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
  611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];

/**
 * "peça" → "peca". Na Helvetica o glifo acentuado tem a MESMA largura de avanço
 * da letra base (é mede o mesmo que e), então tirar o acento e consultar a
 * tabela ASCII dá a largura exata — sem carregar as métricas dos 100 e poucos
 * glifos acentuados do WinAnsi só para centralizar uma linha.
 */
function semAcento(c: string): string {
  return c.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Largura do texto em milímetros, no corpo pedido. */
export function larguraDoTexto(texto: string, tamanho: number, negrito = false): number {
  const tabela = negrito ? LARGURAS_NEGRITO : LARGURAS_NORMAL;
  let milesimos = 0;
  for (const bruto of texto) {
    const c = semAcento(bruto) || bruto;
    const cod = c.charCodeAt(0);
    // Fora do ASCII imprimível (símbolo, emoji): 556 é a largura da maioria das
    // minúsculas da Helvetica — aproximação boa o bastante para alinhamento.
    milesimos += cod >= 32 && cod <= 126 ? tabela[cod - 32]! : 556;
  }
  return (milesimos / 1000) * tamanho / MM;
}

/**
 * As larguras dos glifos do WinAnsi que NÃO são letra ASCII nem letra com
 * acento (que o semAcento resolve): o travessão e as reticências medem 1000,
 * não 556. Pares [normal, negrito], da AFM da Helvetica.
 *
 * Ficam FORA do larguraDoTexto de propósito: o vale alinha e quebra texto com
 * ele, e mudar a medida mudaria os bytes do vale (pdf.ouro.test.ts). Quem
 * precisa da medida certa — a lista de contas, onde a descrição tem de parar
 * antes da coluna do valor e o iPhone troca "--" por "—" sozinho — usa
 * larguraExata (revisão de 22/09/2026).
 */
const LARGURAS_EXTRAS: Record<string, readonly [number, number]> = {
  '€': [556, 556], '‚': [222, 278], 'ƒ': [556, 556], '„': [333, 500], '…': [1000, 1000],
  '†': [556, 556], '‡': [556, 556], 'ˆ': [333, 333], '‰': [1000, 1000], '‹': [333, 333],
  'Œ': [1000, 1000], '‘': [222, 278], '’': [222, 278], '“': [333, 500], '”': [333, 500],
  '•': [350, 350], '–': [556, 556], '—': [1000, 1000], '˜': [333, 333], '™': [1000, 1000],
  '›': [333, 333], 'œ': [944, 944],
  '\u00a0': [278, 278], '¡': [333, 333], '¢': [556, 556], '£': [556, 556], '¤': [556, 556],
  '¥': [556, 556], '¦': [260, 280], '§': [556, 556], '¨': [333, 333], '©': [737, 737],
  'ª': [370, 370], '«': [556, 556], '¬': [584, 584], '\u00ad': [333, 333], '®': [737, 737],
  '¯': [333, 333], '°': [400, 400], '±': [584, 584], '²': [333, 333], '³': [333, 333],
  '´': [333, 333], 'µ': [556, 611], '¶': [537, 556], '·': [278, 278], '¸': [333, 333],
  '¹': [333, 333], 'º': [365, 365], '»': [556, 556], '¼': [834, 834], '½': [834, 834],
  '¾': [834, 834], '¿': [611, 611], 'Æ': [1000, 1000], 'Ð': [722, 722], '×': [584, 584],
  'Ø': [778, 778], 'Þ': [667, 667], 'ß': [611, 611], 'æ': [889, 889], 'ð': [556, 611],
  '÷': [584, 584], 'ø': [611, 611], 'þ': [556, 611],
};

/** Como larguraDoTexto, mas com a largura CERTA dos glifos do WinAnsi fora
 *  do ASCII (travessão, reticências, aspas curvas, °, ©…). */
export function larguraExata(texto: string, tamanho: number, negrito = false): number {
  let extra = 0;
  for (const c of texto) {
    const w = LARGURAS_EXTRAS[c];
    if (w) extra += w[negrito ? 1 : 0] - 556;
  }
  return larguraDoTexto(texto, tamanho, negrito) + (extra / 1000) * tamanho / MM;
}

/**
 * Quebra o parágrafo em linhas que cabem na largura dada.
 *
 * O PDF não tem fluxo de texto: cada linha é posicionada por coordenada. Sem
 * isto, os termos do vale — que são a parte JURÍDICA do documento — sairiam
 * numa única linha cortada na margem, e o que o cliente assina precisa estar
 * legível por inteiro.
 *
 * Palavra maior que a largura (um e-mail comprido) fica sozinha na linha e
 * transborda: cortar no meio esconderia caractere, e num documento assinado
 * dado escondido é pior que dado feio.
 */
export function quebrarEmLinhas(
  texto: string, larguraMm: number, tamanho: number, negrito = false,
): string[] {
  const linhas: string[] = [];
  let atual = '';
  for (const palavra of texto.split(/\s+/).filter(Boolean)) {
    const tentativa = atual === '' ? palavra : `${atual} ${palavra}`;
    if (atual !== '' && larguraDoTexto(tentativa, tamanho, negrito) > larguraMm) {
      linhas.push(atual);
      atual = palavra;
    } else {
      atual = tentativa;
    }
  }
  if (atual !== '') linhas.push(atual);
  return linhas;
}

// --- Codificação --------------------------------------------------------------
// O PDF escreve texto em bytes, e a fonte declara WinAnsiEncoding. De 0xA0 a
// 0xFF o WinAnsi é igual ao Latin-1 — ou seja, á/ç/ã saem de graça. O que muda
// é a faixa 0x80–0x9F, onde a Microsoft pôs aspas curvas, travessões e o €.
const ESPECIAIS_WINANSI: Record<string, number> = {
  '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87,
  'ˆ': 0x88, '‰': 0x89, 'Š': 0x8a, '‹': 0x8b, 'Œ': 0x8c, 'Ž': 0x8e,
  '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97,
  '˜': 0x98, '™': 0x99, 'š': 0x9a, '›': 0x9b, 'œ': 0x9c, 'ž': 0x9e, 'Ÿ': 0x9f,
};

/**
 * Texto para bytes WinAnsi, já com os escapes do PDF.
 *
 * `(`, `)` e `\` delimitam string literal no formato; um nome de cliente com
 * parêntese — "João (oficina do Zé)" — quebraria o arquivo inteiro sem isto.
 * É o mesmo cuidado que o `escaparHtml` do aVerDoc.ts tem no documento HTML.
 */
/** O caractere tem glifo VISÍVEL nas fontes do documento, e o mesmo glifo
 *  que um canvas desenharia? Quem desenha o MESMO texto em outro lugar (a
 *  imagem da lista de contas) usa isto para os dois dizerem a mesma coisa.
 *  Fora ficam os controles — DEL e a faixa U+0080–U+009F, que o paraWinAnsi
 *  gravaria como byte cru e o WinAnsi leria como – ou … — e a quebra de
 *  linha (revisão de 22/09/2026). */
export function representavelEmWinAnsi(c: string): boolean {
  if (ESPECIAIS_WINANSI[c] !== undefined) return true;
  const cod = c.codePointAt(0)!;
  return (cod >= 0x20 && cod <= 0x7e) || (cod >= 0xa0 && cod <= 0xff);
}

export function paraWinAnsi(texto: string): number[] {
  const saida: number[] = [];
  for (const c of texto) {
    let b = ESPECIAIS_WINANSI[c];
    if (b === undefined) {
      const cod = c.codePointAt(0)!;
      // Latin-1 direto; qualquer outra coisa vira '?' em vez de sumir em
      // silêncio — caractere ausente no papel é mais fácil de notar assim.
      b = cod <= 0xff && (cod >= 0x20 || cod === 0x0a) ? cod : 0x3f;
    }
    if (b === 0x28 || b === 0x29 || b === 0x5c) saida.push(0x5c); // ( ) \
    saida.push(b);
  }
  return saida;
}

// --- O documento ---------------------------------------------------------------

export interface Pdf {
  texto(texto: string, xMm: number, yMm: number, opcoes?: OpcoesTexto): void;
  linha(x1: number, y1: number, x2: number, y2: number, opcoes?: OpcoesTraco): void;
  retangulo(xMm: number, yMm: number, larguraMm: number, alturaMm: number, cinza?: number): void;
  /**
   * Desenha um caminho vetorial (o símbolo da marca) preenchido, encaixado
   * numa caixa em mm. `segmentos` vem de interpretarCaminho(); `viewBox` é o
   * do SVG de origem, e é o que dá a escala.
   */
  caminho(
    segmentos: Segmento[],
    viewBox: readonly [number, number, number, number],
    caixa: { xMm: number; yMm: number; larguraMm: number },
    cinza?: number,
  ): void;
  /**
   * Começa uma página nova, do mesmo tamanho; tudo o que se desenha depois cai
   * nela. As coordenadas continuam as da folha (0,0 no canto de cima).
   */
  novaPagina(): void;
  /** Quantas páginas o documento tem até agora (sempre ao menos 1). */
  readonly paginas: number;
  bytes(): Uint8Array;
}

function ascii(s: string): number[] {
  const saida: number[] = [];
  for (let i = 0; i < s.length; i += 1) saida.push(s.charCodeAt(i) & 0xff);
  return saida;
}

/** Quebra de linha do fluxo PDF, como constante: escapes viajam mal por
 *  script, e um `\n` virado em newline de verdade quebra o arquivo. */
const NL = String.fromCharCode(10);

/** Ponto com no máximo 2 casas e sem "-0" nem notação científica. */
const pt = (mm: number): string => {
  const v = Math.round(mm * MM * 100) / 100;
  return Object.is(v, -0) ? '0' : String(v);
};

export function novoPdf(opcoes: { larguraMm?: number; alturaMm?: number } = {}): Pdf {
  const larguraMm = opcoes.larguraMm ?? A4_LARGURA_MM;
  const alturaMm = opcoes.alturaMm ?? A4_ALTURA_MM;

  // Conteúdo em bytes desde já: o texto entra em WinAnsi, que não sobrevive a
  // uma volta por string JavaScript sem virar UTF-8 na hora de serializar.
  // Um fluxo por página; desenha-se sempre na última.
  const fluxos: number[][] = [[]];
  let fluxo = fluxos[0]!;
  const escrever = (s: string) => fluxo.push(...ascii(s));

  // O PDF conta a partir de baixo; o mundo conta a partir de cima.
  const paraBaixo = (yMm: number) => alturaMm - yMm;

  return {
    novaPagina() {
      fluxo = [];
      fluxos.push(fluxo);
    },

    get paginas() {
      return fluxos.length;
    },

    texto(texto, xMm, yMm, o = {}) {
      const tamanho = o.tamanho ?? 10;
      const negrito = o.negrito ?? false;
      const cinza = o.cinza ?? 0;
      if (texto === '') return;

      let x = xMm;
      if (o.alinhamento === 'centro') x -= larguraDoTexto(texto, tamanho, negrito) / 2;
      else if (o.alinhamento === 'direita') x -= larguraDoTexto(texto, tamanho, negrito);

      escrever(`BT ${cinza} g /${negrito ? 'F2' : 'F1'} ${tamanho} Tf `);
      escrever(`1 0 0 1 ${pt(x)} ${pt(paraBaixo(yMm))} Tm (`);
      fluxo.push(...paraWinAnsi(texto));
      escrever(') Tj ET\n');
    },

    linha(x1, y1, x2, y2, o = {}) {
      escrever(`${o.cinza ?? 0} G ${pt(o.espessura ?? 0.2)} w `);
      escrever(`${pt(x1)} ${pt(paraBaixo(y1))} m ${pt(x2)} ${pt(paraBaixo(y2))} l S\n`);
    },

    caminho(segmentos, viewBox, caixa, cinza = 0) {
      const [minX, minY, vbLargura] = viewBox;
      // Uma escala só para os dois eixos: o símbolo não se deforma.
      const escala = caixa.larguraMm / vbLargura;
      // O SVG cresce para BAIXO e o PDF para CIMA — daí o y espelhado dentro
      // da caixa, além do paraBaixo() da página.
      const X = (x: number) => pt(caixa.xMm + (x - minX) * escala);
      const Y = (y: number) => pt(paraBaixo(caixa.yMm + (y - minY) * escala));

      escrever(`${cinza} g` + NL);
      for (const seg of segmentos) {
        if (seg.tipo === 'm') escrever(`${X(seg.x)} ${Y(seg.y)} m` + NL);
        else if (seg.tipo === 'l') escrever(`${X(seg.x)} ${Y(seg.y)} l` + NL);
        else if (seg.tipo === 'c') {
          escrever(`${X(seg.x1)} ${Y(seg.y1)} ${X(seg.x2)} ${Y(seg.y2)} `);
          escrever(`${X(seg.x)} ${Y(seg.y)} c` + NL);
        } else escrever('h' + NL);
      }
      // `f*` (par-ímpar) e não `f`: o S da marca tem contorno interno, e a
      // regra não-zero pintaria o vazado junto.
      escrever('f*' + NL);
    },

    retangulo(xMm, yMm, larguraRet, alturaRet, cinza = 0) {
      // `re` recebe o canto inferior esquerdo — daí o y já descontado da altura.
      escrever(`${cinza} g ${pt(xMm)} ${pt(paraBaixo(yMm + alturaRet))} `);
      escrever(`${pt(larguraRet)} ${pt(alturaRet)} re f\n`);
    },

    bytes() {
      // A NUMERAÇÃO DOS OBJETOS. A 1ª página fica onde sempre esteve (3 e 4,
      // com as fontes em 5 e 6): com uma página só, o arquivo sai byte a byte
      // o de antes. A página k, da 2ª em diante, é o par 2k+3 (Page) e 2k+4
      // (conteúdo), e todas reaproveitam as mesmas duas fontes.
      const pagina = (conteudo: number) =>
        ascii(`<</Type/Page/Parent 2 0 R/Resources<</Font<</F1 5 0 R/F2 6 0 R>>>>/Contents ${conteudo} 0 R>>`);
      const conteudo = (f: number[]) => {
        const corpo = ascii(`<</Length ${f.length}>>\nstream\n`);
        for (const b of f) corpo.push(b);
        for (const b of ascii('\nendstream')) corpo.push(b);
        return corpo;
      };
      const kids = fluxos.map((_, i) => `${i === 0 ? 3 : 2 * (i + 1) + 3} 0 R`).join(' ');
      const objetos: number[][] = [
        ascii('<</Type/Catalog/Pages 2 0 R>>'),
        ascii(`<</Type/Pages/Kids[${kids}]/Count ${fluxos.length}/MediaBox[0 0 ${pt(larguraMm)} ${pt(alturaMm)}]>>`),
        pagina(4),
        conteudo(fluxos[0]!),
        ascii('<</Type/Font/Subtype/Type1/BaseFont/Helvetica/Encoding/WinAnsiEncoding>>'),
        ascii('<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold/Encoding/WinAnsiEncoding>>'),
      ];
      for (let i = 1; i < fluxos.length; i += 1) {
        const k = i + 1;
        objetos.push(pagina(2 * k + 4), conteudo(fluxos[i]!));
      }

      const saida: number[] = [];
      // Comentário com bytes altos logo após o cabeçalho: é a convenção que faz
      // ferramenta de transferência tratar o arquivo como binário e não estragar
      // as quebras de linha.
      saida.push(...ascii('%PDF-1.4\n'), 0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a);

      const posicoes: number[] = [];
      objetos.forEach((corpo, i) => {
        posicoes.push(saida.length);
        saida.push(...ascii(`${i + 1} 0 obj\n`));
        // Byte a byte, e não com `...corpo`: espalhar um fluxo grande como
        // argumentos de push() esbarra no teto de argumentos do motor (o do
        // Safari é o mais baixo), e a lista de contas tem páginas cheias.
        for (const b of corpo) saida.push(b);
        saida.push(...ascii('\nendobj\n'));
      });

      // A xref é uma tabela de DESLOCAMENTOS EM BYTES, com largura fixa de 20
      // caracteres por linha. Errar um byte aqui é o clássico "o arquivo abre
      // no Chrome e não abre no Acrobat" — por isso os offsets vêm do próprio
      // array acima, e não de uma contagem paralela.
      const inicioXref = saida.length;
      saida.push(...ascii(`xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`));
      for (const p of posicoes) saida.push(...ascii(`${String(p).padStart(10, '0')} 00000 n \n`));
      saida.push(...ascii(`trailer\n<</Size ${objetos.length + 1}/Root 1 0 R>>\n`));
      saida.push(...ascii(`startxref\n${inicioXref}\n%%EOF\n`));

      return Uint8Array.from(saida);
    },
  };
}

/**
 * Entrega o arquivo ao usuário. Fica aqui, e não na tela, porque o
 * `URL.revokeObjectURL` esquecido é vazamento de memória que só aparece depois
 * de o balcão emitir vinte vales sem recarregar a página.
 */
export function baixarPdf(bytes: Uint8Array, nomeDoArquivo: string): void {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeDoArquivo.endsWith('.pdf') ? nomeDoArquivo : `${nomeDoArquivo}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Um tique depois: revogar na mesma volta do laço cancela o download em
  // alguns navegadores antes de ele começar.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
